import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ReactComponent as EyeOn } from '../../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../../assets/images/svg/eye-off.svg';
import { ReactComponent as Lock } from '../../../assets/images/svg/lock.svg';

import ModalBody from '../../Modal/ModalBody';
import ModalButtons from '../../Modal/ModalButtons';
import SectionLoader from '../../Loader/SectionLoader';
import { hashedMask, isPositiveInteger } from '../../../util/helpers/ssnHelper';
import themeColors from '../../../assets/styles/themeColors';
import { InputField } from '../../InputFields/Components/InputComponents';
import { ErrorMessage } from '../../InputFields/ControlledInput';
import Paragraph from '../../Paragraph/Paragraph';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import {
  accountTypeKeys,
  accountTypes,
} from '../../../util/enum/api/accountTypes';
import { postUserSsn } from '../../../store/actions/user/userActions';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import { IS_GTM_EVENT, SSN_REQUIRED } from '../../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  storeInSessionStorage,
} from '../../../util/helpers/sessionStorageHelper';
import { useBrokerName } from '../../../util/hooks/userBrokerName';
import { GS_IRS_LINK, HAA_IRS_LINK } from '../../../util/constants';
import Anchor from '../../Anchor/Anchor';
import { selectAuthUser } from '../../../store/selectors/userSelectors';
import { getQuotesForOrderSell } from '../../../store/actions/orders/orderActions';

const IconButton = styled.button`
  outline: none;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  button {
    position: absolute;
    right: 0;
    margin: 11.5px;
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${themeColors.colorInputPlaceholder};
  }

  input {
    padding-right: 42px;
  }
`;

const NoteContainer = styled.div`
  padding: 8px;
  color: ${themeColors.colorBlack};
  background-color: ${themeColors.colorNoteBackground};
  border: 1px solid ${themeColors.colorPrimary};
  border-radius: 4px;
  border-left-width: 6px;
  margin-bottom: 24px;
`;

const InfoSecureContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 4px;
`;

const LockIconWrap = styled.div`
  height: 16px;
  width: 16px;
  margin-right: 4px;
`;

const InfoSecure = styled.span`
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
`;

export const LearnMoreLinkWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  cursor: pointer;
`;

export const LearnMoreLinkText = styled.div`
  font-size: 16px;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${themeColors.colorLinkPrimary};
`;

const SellSsn = ({
  account,
  handleNext,
  handleBack,
  setModalTitle,
  setIsPopupOpen,
  setPopupTitle,
  setPopupMessage,
  wizardData,
  setWizardData,
  vault,
  product,
  isFractionalProduct,
}) => {
  const { t } = useTranslation();
  const broker = useBrokerName();
  const accountType = parseEnumType(accountTypeKeys, account.AccountType);
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const userInfo = useSelector(selectAuthUser);

  const [actualSsn, setActualSsn] = useState('');
  const [maskedSsn, setMaskedSsn] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, _setIsLoading] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setIsLoadingSafe = useCallback((val) => {
    if (isMountedRef.current) {
      _setIsLoading(val);
    }
  }, []);

  const setIsLoadingGetQuotes = () => {};

  const [isGS, setIsGS] = useState('');
  const isJointAccount =
    accountType === accountTypes.JointTenantsInCommon ||
    accountType === accountTypes.JointTenantsWithRightOfSurvivorship;
  const [error, setError] = useState('');
  const isTrust =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.Trust;
  setModalTitle(t('common.irsReportingRequired'));
  const { FirstName, MiddleName, LastName, SuffixType } = userInfo;
  const fullName = `${FirstName || ''} ${MiddleName || ''} ${LastName || ''} ${
    SuffixType || ''
  }`;

  useEffect(() => {
    if (broker) {
      setIsGS(broker === 'GoldSilver');
    }
  }, [broker]);

  useEffect(() => {
    setMaskedSsn(hashedMask(actualSsn, true, isVisible));
  }, [actualSsn, isVisible]);

  const onHandleGetQuotesSuccess = (getQuoteResult) => {
    if (!isMountedRef.current) return;
    const {
      QuoteUid,
      ExpiresOnUtc,
      QuoteStatusItem: {
        QuantityExecuted,
        ClientPricePerUnit,
        TotalClientExecutionPrice,
        VolumeDiscount,
      },
    } = getQuoteResult;

    const calculation = {
      estimatedOrderTotal: TotalClientExecutionPrice,
      newPricePerUnit: ClientPricePerUnit,
      volumeDiscount: VolumeDiscount,
      quantity: QuantityExecuted,
    };

    if (setWizardData) {
      setWizardData({
        ...wizardData,
        calculation,
        quoteID: QuoteUid,
        expiryTime: ExpiresOnUtc,
      });
    }
  };

  const fetchQuotes = (symbolCode) => {
    if (!symbolCode || !account?.AccountUid) return;
    const quantityOrAmount =
      wizardData?.calculation?.quantity ??
      wizardData?.orderValues?.QuantityAmount;

    if (!quantityOrAmount) return;

    const isSegregated = product?.IsSegregated;
    const payload = {
      getQuotesPayload: {
        accountUid: account.AccountUid,
        symbolCode,
        quantityOrAmount,
        sideType: 'Sell',
        unitType: wizardData?.orderType,
        isSegregated,
      },
      isLoading: setIsLoadingGetQuotes,
      onSuccess: onHandleGetQuotesSuccess,
      onError: () => {},
    };
    setIsLoadingGetQuotes(true);
    dispatch(getQuotesForOrderSell(payload));
  };

  // Refresh quotes every 30 seconds while on SSN step (non-fractional only)
  useEffect(() => {
    if (isFractionalProduct) return;
    const symbol = vault?.SymbolCode;
    if (!symbol) return;

    // Kick off an initial refresh and then poll
    fetchQuotes(symbol);
    const id = setInterval(() => fetchQuotes(symbol), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.AccountUid, vault?.SymbolCode, isFractionalProduct]);

  const handleSubmit = () => {
    if (actualSsn === '') {
      return;
    }

    const Ssn = `${actualSsn.slice(0, 3)}-${actualSsn.slice(
      3,
      5,
    )}-${actualSsn.slice(5, 9)}`;

    if (Ssn.length === 2) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinRequired')
          : t('fields.errors.validation.ssnRequired'),
      );
      return;
    }

    if (Ssn.length < 11) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinNotValid')
          : t('fields.errors.validation.ssnNotValid'),
      );
      return;
    }

    if (
      !isPositiveInteger(Ssn.slice(0, 3)) ||
      !isPositiveInteger(Ssn.slice(4, 6)) ||
      !isPositiveInteger(Ssn.slice(7, 11))
    ) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinDigits')
          : t('fields.errors.validation.ssnDigits'),
      );
      return;
    }
    const payload = {
      data: {
        AccountUid: accountUid,
        Tin: Ssn,
      },
      setIsLoading: setIsLoadingSafe,
      onSuccess: () => {
        setIsPopupOpen(false);
        storeInSessionStorage(SSN_REQUIRED, 0);
        handleNext();
      },
      onError: (error) => {
        const errorCode = getErrorCodeFromAPIResponse(error);
        storeInSessionStorage(SSN_REQUIRED, 1);
        setPopupMessage(
          isTrust
            ? t(`irs.tin.error.${errorCode}.message`)
            : t(`irs.ssn.error.${errorCode}.message`),
        );
        setPopupTitle(
          isTrust
            ? t(`irs.tin.error.${errorCode}.title`)
            : t(`irs.ssn.error.${errorCode}.title`),
        );
        setIsPopupOpen(true);
      },
    };
    setIsPopupOpen(false);
    dispatch(postUserSsn(payload));
  };

  const initialValues = {
    Ssn: '',
  };

  const handleChange = (event) => {
    const prevValue = maskedSsn;
    const newValue = event.target.value;
    let actual = actualSsn;

    if (newValue.length > 11) {
      return;
    }

    if (newValue.length > prevValue.length) {
      const newChar = newValue.split('').pop();
      actual = `${actual}${newChar}`;
    } else {
      const charsRemovedCount = prevValue.length - newValue.length;
      actual = actual.substr(0, actual.length - charsRemovedCount);
    }

    if (
      actual.length === 0 ||
      actual.length === 9 ||
      isPositiveInteger(actual) ||
      !isPositiveInteger(actual)
    ) {
      setError('');
    }

    setActualSsn(actual);
    setMaskedSsn(hashedMask(actual, true, isVisible));
  };

  const primaryAccountHolder = fullName;

  const getNoteText = () => {
    if (isJointAccount) {
      return (
        <>
          {t('irs.ssn.primaryHolderNote1')}
          <strong>{primaryAccountHolder} </strong>
          {t('irs.ssn.primaryHolderNote2')}
        </>
      );
    }

    return <>{isTrust ? t('irs.tin.note') : t('irs.ssn.note')}</>;
  };

  return (
    <SectionLoader isLoading={isLoading}>
      <ModalBody>
        <NoteContainer>
          <strong>{t('common.important')}</strong>
          {getNoteText()}
        </NoteContainer>
        <Paragraph bold marginBottom={4} fontSize={16}>
          {isTrust ? t('common.tinFieldTitle') : t('common.ssnFieldTitle')}
        </Paragraph>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur
        >
          {() => (
            <Form>
              <Container>
                <InputField
                  label={t('common.ssnFieldTitle')}
                  type="text"
                  placeholder="XXX-XX-XXXX"
                  color={themeColors.colorBlack}
                  onChange={handleChange}
                  value={maskedSsn}
                  autocomplete="off"
                  autoFocus
                />
                <IconButton
                  type="button"
                  title={`${isVisible ? 'Hide SSN' : 'Show SSN'}`}
                  onClick={() => setIsVisible(!isVisible)}
                >
                  {isVisible ? <EyeOn /> : <EyeOff />}
                </IconButton>
              </Container>
              {error && (
                <ErrorMessage error={error} name="Ssn">
                  {error}
                </ErrorMessage>
              )}
              <InfoSecureContainer>
                <LockIconWrap>
                  <Lock />
                </LockIconWrap>
                <InfoSecure>{t('irs.footNote')}</InfoSecure>
              </InfoSecureContainer>
              <ModalButtons
                marginTop
                buttonStylesReversed
                primaryButtonProps={{
                  label: t('common.goBack'),
                  onClick: () => {
                    handleBack();
                    removeFromSessionStorage(IS_GTM_EVENT);
                  },
                  'data-cy': 'button-cancel',
                  marginTop: 12,
                }}
                secondaryButtonProps={{
                  'data-cy': 'button-next',
                  disabled: maskedSsn?.length !== 11,
                  label: t('common.agreeAndContinue'),
                  type: 'submit',
                }}
              />
              <LearnMoreLinkWrap onPress={() => {}}>
                <LearnMoreLinkText>
                  <Anchor
                    bold
                    type="website"
                    value={isGS ? GS_IRS_LINK : HAA_IRS_LINK}
                    text={t('irs.learnMore')}
                  />
                </LearnMoreLinkText>
              </LearnMoreLinkWrap>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </SectionLoader>
  );
};

SellSsn.propTypes = {
  account: PropTypes.shape({
    AccountType: PropTypes.number,
    DisplayName: PropTypes.string,
    AccountCode: PropTypes.string,
    AccountUid: PropTypes.string,
  }),
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  setModalTitle: PropTypes.func,
  setIsPopupOpen: PropTypes.func,
  setPopupTitle: PropTypes.func,
  setPopupMessage: PropTypes.func,
  wizardData: PropTypes.shape({
    calculation: PropTypes.shape({
      estimatedOrderTotal: PropTypes.number,
      newPricePerUnit: PropTypes.number,
      volumeDiscount: PropTypes.number,
      quantity: PropTypes.number,
    }),
    orderValues: PropTypes.shape({
      QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    orderType: PropTypes.string,
    quoteID: PropTypes.string,
    expiryTime: PropTypes.string,
  }),
  setWizardData: PropTypes.func,
  vault: PropTypes.shape({
    SymbolCode: PropTypes.string,
  }),
  product: PropTypes.shape({
    IsSegregated: PropTypes.bool,
  }),
  isFractionalProduct: PropTypes.bool,
};

export default SellSsn;
